const PHOTO_LINK_SALT = "clinicos-photo-link-v1";
const PHOTO_LINK_PREFIX = "p_";

const MODULUS = 2147483647n; // prime: 2^31 - 1

const fnv1a = (input: string): number => {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const parseBase36BigInt = (value: string): bigint | null => {
  if (!/^[0-9a-z]+$/i.test(value)) return null;
  let result = 0n;
  const normalized = value.toLowerCase();
  for (let i = 0; i < normalized.length; i++) {
    const code = normalized.charCodeAt(i);
    const digit = code >= 48 && code <= 57 ? code - 48 : code - 87;
    if (digit < 0 || digit > 35) return null;
    result = result * 36n + BigInt(digit);
  }
  return result;
};

const modPow = (base: bigint, exponent: bigint, modulus: bigint): bigint => {
  let result = 1n;
  let b = base % modulus;
  let e = exponent;

  while (e > 0n) {
    if (e % 2n === 1n) result = (result * b) % modulus;
    b = (b * b) % modulus;
    e /= 2n;
  }
  return result;
};

const MULTIPLIER = BigInt((fnv1a(`${PHOTO_LINK_SALT}:a`) % 2147483645) + 1);
const OFFSET = BigInt(fnv1a(`${PHOTO_LINK_SALT}:b`) % 2147483647);
const MULTIPLIER_INVERSE = modPow(MULTIPLIER, MODULUS - 2n, MODULUS);

const encodeNumericId = (id: number): string => {
  const n = BigInt(id);
  const obfuscated = (MULTIPLIER * n + OFFSET) % MODULUS;
  return `${PHOTO_LINK_PREFIX}${obfuscated.toString(36)}`;
};

const decodeTokenToNumericId = (token: string): number | null => {
  if (!token.startsWith(PHOTO_LINK_PREFIX)) return null;
  const encoded = token.slice(PHOTO_LINK_PREFIX.length);
  const obfuscated = parseBase36BigInt(encoded);
  if (obfuscated === null || obfuscated <= 0n || obfuscated >= MODULUS) {
    return null;
  }

  const shifted = (obfuscated - OFFSET + MODULUS) % MODULUS;
  const decoded = (shifted * MULTIPLIER_INVERSE) % MODULUS;
  const asNumber = Number(decoded);
  if (!Number.isSafeInteger(asNumber) || asNumber <= 0) return null;

  return asNumber;
};

const normalizeRawNumericId = (value: string | number): number | null => {
  const parsed =
    typeof value === "number" ? value : Number.parseInt(value.trim(), 10);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) return null;
  return parsed;
};

const encodePhotoLinkId = (imageId: string | number): string => {
  const numeric = normalizeRawNumericId(imageId);
  if (numeric === null) return encodeURIComponent(String(imageId));
  return encodeNumericId(numeric);
};

export const resolvePhotoLinkTargetId = (
  encodedOrRawId: string | number,
): string | null => {
  const raw = String(encodedOrRawId).trim();
  if (!raw) return null;

  const decoded = decodeTokenToNumericId(raw);
  if (decoded !== null) return String(decoded);

  const numericRaw = normalizeRawNumericId(raw);
  if (numericRaw !== null) return String(numericRaw);

  return raw;
};

export const buildPhotoDeepLinkPath = (imageId: string | number): string => {
  return `/photo/${encodePhotoLinkId(imageId)}`;
};

export const buildPhotoDeepLinkUrl = (
  imageId: string | number,
  origin?: string,
): string => {
  const path = buildPhotoDeepLinkPath(imageId);
  const resolvedOrigin =
    origin ?? (typeof window !== "undefined" ? window.location.origin : "");

  return resolvedOrigin ? `${resolvedOrigin}${path}` : path;
};

export const matchesPhotoImageId = (
  candidateId: string | number | null | undefined,
  targetId: string,
): boolean => {
  if (candidateId === null || candidateId === undefined) return false;
  return String(candidateId) === targetId;
};
